import { Link } from 'react-router-dom';
import { useUser } from '@/queries/useUser';
import { cn } from '@/lib';
import IcEdit from '@/assets/icons/ic-edit-01.svg';
import { constructSocialUrl, getSocialPlatformConfig } from '@/features/profile/constants/socialPlatforms';
import type { SocialPlatform } from '@/features/profile/types/profile.types';
import { SocialIcon } from './SocialIcon';
import styles from './StoreProfileHeader.module.css';

const SOCIAL_ICONS_ORDERING: SocialPlatform[] = [
    'instagram',
    'tiktok',
    'email',
    'facebook',
    'youtube',
    'link',
    'pinterest',
    'linkedin',
    'twitter',
    'spotify',
    'apple-podcasts',
    'etsy',
    'discord',
    'snapchat',
    'twitch',
    'vimeo',
];

export const StoreProfileHeader = () => {
    const { user } = useUser();

    if (!user) return null;

    // Get defined social links from user data
    const userSocials = user.data?.socials || {};
    const definedSocials = SOCIAL_ICONS_ORDERING.filter(platform => {
        const value = userSocials[platform];
        return value && value.length > 0;
    });

    return (
        <div className='bg-canvas shadow-stan group relative mb-6 flex flex-nowrap gap-3 rounded-lg border border-transparent p-4 transition md:gap-4'>
            {/* Invisible Link Overlay - covers entire card except social icons */}
            <Link
                to='/profile'
                className='focus-visible:border-brand-main absolute inset-0 rounded-lg border border-transparent transition'
                aria-label='Edit profile'
            />

            {/* Profile Image */}
            <div className='shrink-0'>
                <img
                    src={user.profile_image || '/images/default-avatar.png'}
                    alt={user.full_name || 'Profile'}
                    className='size-25 rounded-md object-cover'
                />
            </div>

            {/* Profile Details */}
            <div className='flex w-full min-w-0 flex-1 flex-col justify-between'>
                <div>
                    {/* Name and Edit Icon */}
                    <div className='flex items-center gap-1'>
                        <p className='text-dark truncate font-bold'>{user.full_name}</p>
                        <IcEdit className='fill-semantic-fg-subtle group-focus-visible:fill-semantic-fg-static-dark group-hover:fill-semantic-fg-static-dark pointer-events-none size-5 shrink-0 rounded-sm transition group-hover:rotate-6' />
                    </div>

                    {/* Username */}
                    <p className='text-body-md text-gray mb-2'>@{user.username}</p>
                </div>

                {/* Social Icons */}
                {definedSocials.length > 0 && (
                    <div
                        className={cn(
                            styles.socialIconsContainer,
                            'relative z-10 mt-auto flex items-center gap-2 max-md:overflow-hidden'
                        )}
                    >
                        {definedSocials.map(platform => {
                            const config = getSocialPlatformConfig(platform);
                            const value = userSocials[platform];

                            if (!config || !value) return null;

                            // Construct the URL based on platform type
                            let href = value;
                            if (config.inputType === 'username' && config.baseUrl) {
                                href = constructSocialUrl(platform, value);
                            } else if (platform === 'email') {
                                href = `mailto:${value}`;
                            }

                            return (
                                <a
                                    key={platform}
                                    href={href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={cn(
                                        styles.socialIcon,
                                        'bg-neutral-subtle flex size-8 shrink-0 items-center justify-center rounded-md',
                                        'hover:bg-neutral-primary transition-all duration-200 ease-out',
                                        'active:translate-y-0 active:scale-95'
                                    )}
                                >
                                    <SocialIcon
                                        platform={platform}
                                        className='text-dark size-4 transition-transform duration-200 ease-out'
                                    />
                                </a>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
